import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Mail, Calendar, AlertCircle, ChevronDown, ChevronRight, Search, ArrowLeft, Plane } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import ReservaDetail from "@/pages/trips/ReservaDetail";
import TripEmailComposer from "@/pages/trips/TripEmailComposer";
import { ConnectEmailDialog } from "@/pages/trips/AsistenteTab";
import { ReconDaySubgroups } from "@/pages/trips/ReconfirmacionesTab";
import { fmtDayHeader, todayISO, addDaysISO } from "@/pages/trips/reconfirmShared";

const ALL = "__all__";

function useEstadoMut(qc) {
  return useMutation({
    mutationFn: async ({ rid, estado }) => (await api.patch(`/reconfirmaciones/reserva/${rid}`, { estado })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon-daily"] });
      qc.invalidateQueries({ queryKey: ["recon-trip"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
}

function groupByDay(reservas) {
  const groups = {};
  reservas.forEach((r) => { const k = r.date_entry || ""; (groups[k] = groups[k] || []).push(r); });
  return Object.entries(groups).sort(([a], [b]) => (a || "9999").localeCompare(b || "9999"));
}

// Abre la ficha completa de la reserva (reutiliza ReservaDetail) y permite volver.
function ReservaOpener({ tripId, reservaId, onBack }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["trip", tripId], queryFn: async () => (await api.get(`/trips/${tripId}`)).data });
  if (isLoading || !data) return <div className="space-y-3"><Skeleton className="h-9 w-48" /><Skeleton className="h-64 w-full" /></div>;
  const { trip, reservas, recordatorios } = data;
  const reserva = (reservas || []).find((r) => r.id === reservaId);
  if (!reserva) {
    return (
      <Card><CardContent className="space-y-3 p-10 text-center">
        <p className="text-sm text-muted-foreground">No se encontró la reserva.</p>
        <Button variant="outline" onClick={onBack}><ArrowLeft className="mr-1.5 h-4 w-4" />Volver</Button>
      </CardContent></Card>
    );
  }
  return (
    <ReservaDetail tripId={tripId} trip={trip} reserva={reserva} recordatorios={recordatorios || []}
      onBack={onBack}
      onChanged={() => {
        qc.invalidateQueries({ queryKey: ["trip", tripId] });
        qc.invalidateQueries({ queryKey: ["recon-daily"] });
        qc.invalidateQueries({ queryKey: ["recon-trip", tripId] });
      }} />
  );
}

// Listado de reconfirmaciones de un viaje concreto (al pinchar el nombre del viajero).
function TripReconPanel({ tripId, clientName, onBack, onOpenReserva, onSend, estadoMut }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const { data, isLoading } = useQuery({ queryKey: ["recon-trip", tripId], queryFn: async () => (await api.get(`/reconfirmaciones/trip/${tripId}`)).data });
  const reservas = useMemo(() => data?.reservas || [], [data]);
  const days = useMemo(() => groupByDay(reservas), [reservas]);
  const emailConnected = !!data?.email_connected;
  const pend = reservas.filter((r) => r.estado !== "reconfirmado").length;
  const toggleDay = (k) => setCollapsed((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  return (
    <div className="space-y-4" data-testid="recon-trip-panel">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={onBack} data-testid="recon-trip-back"><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="flex items-center gap-2 font-heading text-xl font-bold tracking-tight"><Plane className="h-5 w-5 text-primary" />{clientName}</h1>
          <p className="text-sm text-muted-foreground">Reconfirmaciones de este viaje · {reservas.length} servicio(s) · {pend} sin reconfirmar</p>
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : reservas.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Este viaje no tiene servicios que reconfirmar.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {days.map(([dayKey, rows]) => {
            const isCol = collapsed.has(dayKey);
            return (
              <Card key={dayKey || "nofecha"} className="overflow-hidden">
                <button onClick={() => toggleDay(dayKey)} className="flex w-full items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-left hover:bg-muted/70">
                  {isCol ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">{fmtDayHeader(dayKey)}</span>
                  <span className="text-xs text-muted-foreground">· {rows.length} servicio(s)</span>
                </button>
                {!isCol && (
                  <ReconDaySubgroups rows={rows} rowProps={(r) => ({
                    selected: false, onToggle: () => {}, emailConnected, estadoMut,
                    onSend: () => onSend({ tripId, multi: false, reservaIds: [r.id], providerId: r.provider_id, toEmail: r.provider_email, providerName: r.provider_name }),
                    onOpenReserva: () => onOpenReserva(r),
                  })} />
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ReconfirmacionesPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(addDaysISO(todayISO(), 2));
  const [agent, setAgent] = useState(ALL);
  const [channel, setChannel] = useState(ALL);
  const [estado, setEstado] = useState(ALL);
  const [q, setQ] = useState("");
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const [composer, setComposer] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [openReserva, setOpenReserva] = useState(null); // { tripId, reservaId }
  const [openTrip, setOpenTrip] = useState(null);        // { tripId, clientName }

  const estadoMut = useEstadoMut(qc);
  const filtersQ = useQuery({ queryKey: ["recon-filters"], queryFn: async () => (await api.get("/reconfirmaciones/filters")).data });
  const params = useMemo(() => ({
    date_from: from, date_to: to,
    agent: agent === ALL ? undefined : agent,
    channel: channel === ALL ? undefined : channel,
    estado: estado === ALL ? undefined : estado,
    q: q.trim() || undefined,
  }), [from, to, agent, channel, estado, q]);

  const dataQ = useQuery({
    queryKey: ["recon-daily", params],
    queryFn: async () => (await api.get("/reconfirmaciones/daily", { params })).data,
    enabled: !!from && !!to && !openReserva && !openTrip,
  });
  const data = dataQ.data;
  const reservas = useMemo(() => data?.reservas || [], [data]);
  const emailConnected = !!data?.email_connected;
  const days = useMemo(() => groupByDay(reservas), [reservas]);
  const pendientes = reservas.filter((r) => r.estado !== "reconfirmado").length;

  const quick = (nDays) => { const t = todayISO(); setFrom(t); setTo(addDaysISO(t, nDays - 1)); };
  const toggleDay = (k) => setCollapsedDays((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const onCloseComposer = (refresh) => { setComposer(null); if (refresh) { qc.invalidateQueries({ queryKey: ["recon-daily"] }); qc.invalidateQueries({ queryKey: ["recon-trip"] }); } };

  // Vista: ficha de reserva
  if (openReserva) {
    return (
      <div data-testid="reconfirmaciones-page">
        <ReservaOpener tripId={openReserva.tripId} reservaId={openReserva.reservaId} onBack={() => setOpenReserva(null)} />
      </div>
    );
  }

  // Vista: reconfirmaciones de un viaje (al pinchar el viajero)
  if (openTrip) {
    return (
      <div data-testid="reconfirmaciones-page">
        <TripReconPanel
          tripId={openTrip.tripId} clientName={openTrip.clientName}
          onBack={() => setOpenTrip(null)} estadoMut={estadoMut}
          onOpenReserva={(r) => setOpenReserva({ tripId: r.trip_id || openTrip.tripId, reservaId: r.id })}
          onSend={(c) => setComposer(c)} />
        {composer && (
          <TripEmailComposer open={!!composer} onClose={onCloseComposer} tripId={composer.tripId}
            providerId={composer.providerId} reservaIds={composer.reservaIds} toEmail={composer.toEmail}
            providerName={composer.providerName} multi={false} emailConnected
            apiBase="/reconfirmaciones" titleSingle="Reconfirmar servicio" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reconfirmaciones-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><CalendarCheck className="h-6 w-6 text-primary" />Reconfirmaciones</h1>
          <p className="text-sm text-muted-foreground">Servicios del equipo por fecha. Reconfirma con al menos 48 h de antelación.</p>
        </div>
        {emailConnected ? (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm" data-testid="recon-page-email-connected">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Enviando como <span className="font-medium">{data.sender_email}</span>
            <Button variant="ghost" size="sm" className="ml-1 h-7" onClick={() => setShowConnect(true)} data-testid="recon-page-change-email">Cambiar</Button>
          </div>
        ) : (
          <Button onClick={() => setShowConnect(true)} data-testid="recon-page-connect-email"><Mail className="mr-1.5 h-4 w-4" />Conectar mi correo</Button>
        )}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="recon-date-from" /></div>
            <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="recon-date-to" /></div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="h-9" onClick={() => quick(1)} data-testid="recon-quick-1">Hoy</Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => quick(2)} data-testid="recon-quick-2">2 días</Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => quick(3)} data-testid="recon-quick-3">3 días</Button>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Agente / Soporte</Label>
              <Select value={agent} onValueChange={setAgent}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="recon-filter-agent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todo el equipo</SelectItem>
                  {(filtersQ.data?.agents || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Canal</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="recon-filter-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  <SelectItem value="mail">Mail</SelectItem>
                  <SelectItem value="telefono">Teléfono</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="voucher">Voucher</SelectItem>
                  <SelectItem value="web">Web</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="recon-filter-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="solicitada">Solicitada</SelectItem>
                  <SelectItem value="reconfirmado">Reconfirmado (F)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative min-w-[200px] flex-1 space-y-1">
              <Label className="text-xs">Buscar</Label>
              <Search className="absolute left-2.5 top-[30px] h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, servicio, proveedor…" className="h-9 pl-8" data-testid="recon-search" />
            </div>
          </div>
        </CardContent>
      </Card>

      {!emailConnected && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" /> Conecta tu correo para poder enviar las reconfirmaciones por email.
        </div>
      )}

      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="recon-summary">
        <Badge variant="secondary">{reservas.length} servicio(s)</Badge>
        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{pendientes} sin reconfirmar</Badge>
        <span className="text-xs">· Pincha el nombre del viajero para ver todo su viaje, o el servicio para abrir la reserva.</span>
      </div>

      {dataQ.isLoading ? (
        <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : reservas.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="recon-page-empty">No hay servicios en este rango de fechas con los filtros aplicados.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {days.map(([dayKey, rows]) => {
            const isCol = collapsedDays.has(dayKey);
            const dayPend = rows.filter((r) => r.estado !== "reconfirmado").length;
            return (
              <Card key={dayKey || "nofecha"} className="overflow-hidden" data-testid={`recon-page-day-${dayKey || "nofecha"}`}>
                <button onClick={() => toggleDay(dayKey)} className="flex w-full items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-left hover:bg-muted/70">
                  {isCol ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">{fmtDayHeader(dayKey)}</span>
                  <span className="text-xs text-muted-foreground">· {rows.length} servicio(s)</span>
                  {dayPend > 0 && <Badge variant="outline" className="ml-1 border-amber-200 bg-amber-50 text-[10px] text-amber-700">{dayPend} pendiente(s)</Badge>}
                </button>
                {!isCol && (
                  <ReconDaySubgroups rows={rows} rowProps={(r) => ({
                    selected: false, onToggle: () => {}, showClient: true, emailConnected, estadoMut,
                    onSend: () => setComposer({ tripId: r.trip_id, multi: false, reservaIds: [r.id], providerId: r.provider_id, toEmail: r.provider_email, providerName: r.provider_name }),
                    onOpenReserva: () => setOpenReserva({ tripId: r.trip_id, reservaId: r.id }),
                    onOpenTrip: () => setOpenTrip({ tripId: r.trip_id, clientName: r.client_name }),
                  })} />
                )}
              </Card>
            );
          })}
        </div>
      )}

      {composer && (
        <TripEmailComposer open={!!composer} onClose={onCloseComposer} tripId={composer.tripId}
          providerId={composer.providerId} reservaIds={composer.reservaIds} toEmail={composer.toEmail}
          providerName={composer.providerName} multi={false} emailConnected={emailConnected}
          senderEmail={data?.sender_email} apiBase="/reconfirmaciones" titleSingle="Reconfirmar servicio" />
      )}

      <ConnectEmailDialog open={showConnect} onClose={() => setShowConnect(false)} currentEmail={data?.sender_email}
        onConnected={() => qc.invalidateQueries({ queryKey: ["recon-daily"] })} />
    </div>
  );
}
